import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, filter, of, switchMap, take, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  DeleteDialogData
} from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsStateModel } from '../../../core/state/fetchOptions.state';
import {
  LearningSectionContainerJsonModel
} from '../../learning-section-container/learning-section-container.json.model';
import { LearningSectionContainerService } from '../../learning-section-container/learning-section-container.service';
import { LearningSectionContainerActions } from './learning-section-container.actions';
import {
  LearningSectionContainerEditComponent
} from '../../learning-section-container/learning-section-container-edit/learning-section-container-edit.component';
import { map } from 'rxjs/operators';
import { IRawModel } from '@datx/utils';
import { LearningSectionActions } from '../learning-section/learning-section.actions';
import { LearningSectionState } from '../learning-section/learning-section.state';
import { LearningSectionJsonModel } from '../../learning-section.json.model';
import {
  LearningSectionContainerSingleEditComponent
} from '../../learning-section-container/learning-section-container-single-edit/learning-section-container-single-edit.component';
import { ExplanationContentJsonModel } from '../../../explanation/content/explanation-content.json.model';
import { ExplanationContentActions } from '../../../explanation/ngxs/explanation-content/explanation-content.actions';

export interface LearningSectionContainerStateModel {
  learningSectionContainers: LearningSectionContainerJsonModel[]
  learningSectionContainer: LearningSectionContainerJsonModel | null
}

@State<LearningSectionContainerStateModel>({
  name: 'learningSectionContainer',
  defaults: {
    learningSectionContainers: [],
    learningSectionContainer: null,
  }
})
@Injectable()
export class LearningSectionContainerState {
  private readonly learningSectionContainerService = inject(LearningSectionContainerService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: LearningSectionContainerStateModel) {
    return state;
  }

  @Selector()
  static getLearningSectionContainers(state: LearningSectionContainerStateModel) {
    return state.learningSectionContainers;
  }

  @Selector()
  static getLearningSectionContainer(state: LearningSectionContainerStateModel) {
    return state.learningSectionContainer;
  }

  @Selector()
  static getAmountOfLearningSectionContainers(state: LearningSectionContainerStateModel) {
    return state.learningSectionContainers.length;
  }

  @Selector()
  static learningSectionContainerIsEmpty(state: LearningSectionContainerStateModel) {
    return state.learningSectionContainers.length <= 0;
  }

  @Action(LearningSectionContainerActions.Fetch)
  onFetchLearningSectionContainers(ctx: StateContext<LearningSectionContainerStateModel>, action: LearningSectionContainerActions.Fetch) {
    const fetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...fetchOptions,
      ...restOverrides,
      sortFieldName: 'title'
    };
    return this.learningSectionContainerService.getAllWithPagination({
      queryParams: {
        include: 'content'
      }
    }, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res =>
        ctx.dispatch(new LearningSectionContainerActions.FetchSuccess(res[0]))
      )
    );
  }

  @Action(LearningSectionContainerActions.FetchSuccess)
  onFetchLearningSectionContainersSuccess(ctx: StateContext<LearningSectionContainerStateModel>, { payload }: LearningSectionContainerActions.FetchSuccess) {
    ctx.setState({ ...ctx.getState(), learningSectionContainers: payload });
  }

  @Action(LearningSectionContainerActions.FetchOne)
  onFetchOneLearningSectionContainer(ctx: StateContext<LearningSectionContainerStateModel>, action: LearningSectionContainerActions.FetchOne) {
    return this.learningSectionContainerService.getOneWithId(action.id, {
      queryParams: {
        include: 'content'
      }
    }).pipe(
      tap((res: LearningSectionContainerJsonModel) => {
        if (res.content instanceof ExplanationContentJsonModel) {
          ctx.dispatch(new ExplanationContentActions.FetchOne(res.content.id));
        }
        ctx.dispatch(new LearningSectionContainerActions.FetchOneSuccess(res));
      })
    );
  }

  @Action(LearningSectionContainerActions.FetchOneSuccess)
  onFetchOneSuccess(ctx: StateContext<LearningSectionContainerStateModel>, { payload }: LearningSectionContainerActions.FetchOneSuccess) {
    ctx.setState({ ...ctx.getState(), learningSectionContainer: payload });
  }

  @Action(LearningSectionContainerActions.Add)
  onAdd(ctx: StateContext<LearningSectionContainerStateModel>, { payload }: LearningSectionContainerActions.Add) {
    return this.dialog.open<LearningSectionContainerEditComponent, LearningSectionContainerJsonModel, LearningSectionContainerJsonModel>(LearningSectionContainerEditComponent, {
      data: payload,
      width: '90vw',
      height: '90vh',
      maxWidth: '90vw',
      panelClass: 'learning-section-container-full-screen-dialog'
    })
      .afterClosed()
      .pipe(
        filter((learningSectionContainer): learningSectionContainer is LearningSectionContainerJsonModel => !!learningSectionContainer),
        switchMap(learningSectionContainer => this.learningSectionContainerService.createAndSave(learningSectionContainer)),
        catchError(() => of()),
        tap(() => ctx.dispatch(new LearningSectionContainerActions.Fetch({
          pageNumber: 1,
          pageSize: -1,
          skipStoreDispatch: true
        })))
      );
  }

  @Action(LearningSectionContainerActions.AddRaw)
  onAddRaw(ctx: StateContext<LearningSectionContainerStateModel>, { payload }: LearningSectionContainerActions.AddRaw) {
    return this.store.select(LearningSectionState.getLearningSection).pipe(
      filter((section): section is LearningSectionJsonModel => !!section),
      take(1),
      map(section => {
        const rawModel: IRawModel = {
          type: 'learning-section-containers',
          attributes: {
            title: payload.title
          },
          relationships: {
            learningSections: {
              data: [{ type: 'learning-sections', id: section.id }]
            }
          }
        };
        return { rawModel, sectionId: section.id };
      }),
      switchMap(({ rawModel, sectionId }) =>
        this.learningSectionContainerService.createAndSave(rawModel).pipe(
          tap(() => {
            ctx.dispatch(new LearningSectionActions.FetchOne(sectionId));
          })
        )
      )
    );
  }

  @Action(LearningSectionContainerActions.AddRaw)
  onFetchLearningSectionOnAdd(ctx: StateContext<LearningSectionContainerStateModel>) {
    return this.store.select(LearningSectionState.getLearningSection).pipe(
      filter((section): section is LearningSectionJsonModel => !!section),
      tap((section) => ctx.dispatch(new LearningSectionActions.FetchOne(section.id)))
    );
  }

  @Action(LearningSectionContainerActions.Edit)
  onEdit(ctx: StateContext<LearningSectionContainerStateModel>, { payload }: LearningSectionContainerActions.Edit) {
    return this.dialog.open<LearningSectionContainerEditComponent, LearningSectionContainerJsonModel, LearningSectionContainerJsonModel>(LearningSectionContainerEditComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((learningSectionContainer): learningSectionContainer is LearningSectionContainerJsonModel => !!learningSectionContainer),
        switchMap(learningSectionContainer => this.learningSectionContainerService.update(learningSectionContainer)),
        catchError(() => of()),
        tap(() => ctx.dispatch(new LearningSectionContainerActions.Fetch({
          pageNumber: 1,
          pageSize: -1,
          skipStoreDispatch: true
        })))
      );
  }

  @Action(LearningSectionContainerActions.EditRaw)
  onEditRaw(ctx: StateContext<LearningSectionContainerStateModel>, { payload }: LearningSectionContainerActions.EditRaw) {
    return this.learningSectionContainerService.update(payload)
      .pipe(
        tap(() => ctx.dispatch(new LearningSectionContainerActions.Fetch())),
        catchError((error) => {
          console.error('Update failed:', error);
          return of();
        })
      );
  }

  @Action(LearningSectionContainerActions.Update)
  onUpdate(ctx: StateContext<LearningSectionContainerStateModel>, { payload }: LearningSectionContainerActions.Update) {
    return this.learningSectionContainerService.update(payload).pipe(
      switchMap(() =>
        this.store.select(LearningSectionState.getLearningSection).pipe(
          filter((section): section is LearningSectionJsonModel => !!section),
          tap(section => ctx.dispatch(new LearningSectionActions.FetchOne(section.id))),
          take(1)
        )
      ),
      tap(() => ctx.dispatch(new LearningSectionContainerActions.Fetch({
        pageNumber: 1,
        pageSize: -1,
        skipStoreDispatch: true
      })))
    );
  }

  @Action(LearningSectionContainerActions.EditSingle)
  onEditSingle(ctx: StateContext<LearningSectionContainerStateModel>, { payload }: LearningSectionContainerActions.EditSingle) {
    return this.dialog.open<LearningSectionContainerSingleEditComponent, LearningSectionContainerJsonModel, LearningSectionContainerJsonModel>(
      LearningSectionContainerSingleEditComponent,
      {
        data: payload,
        width: '90vw',
        maxHeight: '90vh',
        maxWidth: '90vw'
      }
    )
      .afterClosed()
      .pipe(
        filter((learningSectionContainer): learningSectionContainer is LearningSectionContainerJsonModel => !!learningSectionContainer),
        switchMap(learningSectionContainer => this.learningSectionContainerService.update(learningSectionContainer)),
        catchError(() => of()),
        switchMap((updatedContainer) =>
          this.store.select(LearningSectionState.getLearningSection).pipe(
            filter((section): section is LearningSectionJsonModel => !!section),
            tap((section) => ctx.dispatch(new LearningSectionActions.FetchOne(section.id))),
            map(() => updatedContainer)
          )
        ),
        tap(() => ctx.dispatch(new LearningSectionContainerActions.Fetch({
          pageNumber: 1,
          pageSize: -1,
          skipStoreDispatch: true
        })))
      );
  }

  @Action(LearningSectionContainerActions.Delete)
  onDelete(ctx: StateContext<LearningSectionContainerJsonModel>, { payload }: LearningSectionContainerActions.Delete) {
    return this.store.select(LearningSectionState.getLearningSection).pipe(
      take(1),
      filter((section): section is LearningSectionJsonModel => !!section),
      switchMap((learningSection) =>
        this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
          data: {
            id: payload.id,
            name: payload.title,
            itemType: 'Lernabschnitt Container',
            deleteFn: () => {
              return payload.destroy();
            }
          }
        })
          .afterClosed()
          .pipe(
            tap((success) => {
              if (success) {
                ctx.dispatch(new LearningSectionContainerActions.Fetch({
                  pageNumber: 1,
                  pageSize: -1,
                  skipStoreDispatch: true
                }));
                ctx.dispatch(new LearningSectionActions.FetchOne(learningSection.id));
              }
              else {
                console.log('Deletion was cancelled');
              }
            }),
            catchError((error) => {
              console.error('Error while deleting', error);
              return of(false);
            }),
            map((success) => success)
          )
      )
    );
  }

  @Action(LearningSectionContainerActions.Delete)
  onDeleteContainerFetchLearningSection(ctx: StateContext<LearningSectionContainerJsonModel>) {
    return this.store.select(LearningSectionState.getLearningSection).pipe(
      filter((section): section is LearningSectionJsonModel => !!section),
      tap((section) => ctx.dispatch(new LearningSectionActions.FetchOne(section.id)))
    );
  }
}
